import {
  type BoneDescriptor,
  type BoneDisplayShape,
  type QuatXYZW,
  type Vec3
} from '../../../features/skeleton/SkeletonDescriptor'
import { would_create_cycle } from '../../../features/skeleton/SkeletonValidator'

/**
 * Pure in-memory operations on a `BoneDescriptor[]`. Every operation returns a
 * new array — never mutates the input. Used by `StepBuildSkeleton` which feeds
 * the result through its undo/redo stack.
 */

export class BoneTreeEditor {
  static add_root_bone (
    bones: BoneDescriptor[],
    name: string,
    head: Vec3,
    tail: Vec3
  ): BoneDescriptor[] {
    return [...bones, { name, parent_index: null, head, tail, rotation_quaternion: [0, 0, 0, 1] }]
  }

  static add_child_bone (
    bones: BoneDescriptor[],
    parent_index: number,
    name: string,
    head: Vec3,
    tail: Vec3
  ): BoneDescriptor[] {
    if (parent_index < 0 || parent_index >= bones.length) {
      throw new Error(`add_child_bone: parent_index ${parent_index} out of range.`)
    }
    return [...bones, { name, parent_index, head, tail, rotation_quaternion: [0, 0, 0, 1] }]
  }

  /**
   * Delete the bone at `index` and reparent its direct children to the
   * deleted bone's parent (effectively "remove this single bone from the
   * chain"). Children's `head` and `rotation_quaternion` are transformed
   * from the deleted bone's local space into the new parent's local space
   * so each child stays in the same world position and orientation it had
   * before the delete.
   *
   * If the deleted bone was a root, its children become roots themselves.
   * The deleted bone's `tail` is intrinsic to itself and discarded; child
   * tails are in the child's own local frame and need no transformation.
   */
  static delete_bone_reparent_children (bones: BoneDescriptor[], index: number): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`delete_bone_reparent_children: index ${index} out of range.`)
    }

    const deleted = bones[index]
    // First pass: transform direct children's local-frame data into the
    // deleted bone's parent frame, and reparent them to that grandparent.
    const transformed = bones.map((bone) => {
      if (bone.parent_index !== index) return bone
      const new_head = transform_point_into_parent_frame(bone.head, deleted.head, deleted.rotation_quaternion)
      const new_rotation = quaternion_multiply(deleted.rotation_quaternion, bone.rotation_quaternion)
      return { ...bone, parent_index: deleted.parent_index, head: new_head, rotation_quaternion: new_rotation }
    })

    // Second pass: drop the deleted bone and shift parent indices > index down
    // by one (since indices renumber after the splice).
    return transformed
      .filter((_, i) => i !== index)
      .map((bone) => ({
        ...bone,
        parent_index:
          bone.parent_index === null
            ? null
            : bone.parent_index < index
              ? bone.parent_index
              : bone.parent_index - 1
      }))
  }

  /**
   * Delete the bone at `index` AND all of its descendants. Indices for
   * surviving bones are renumbered; parent_index references are remapped.
   */
  static delete_bone_and_descendants (bones: BoneDescriptor[], index: number): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`delete_bone_and_descendants: index ${index} out of range.`)
    }

    const to_delete = collect_descendant_indices(bones, index)
    to_delete.add(index)

    // Build old-index → new-index map for survivors, in original order.
    const survivor_indices: number[] = []
    for (let i = 0; i < bones.length; i++) {
      if (!to_delete.has(i)) survivor_indices.push(i)
    }
    const old_to_new = new Map<number, number>()
    survivor_indices.forEach((old_i, new_i) => { old_to_new.set(old_i, new_i) })

    return survivor_indices.map(old_i => {
      const old_bone = bones[old_i]
      const new_parent = old_bone.parent_index === null ? null : (old_to_new.get(old_bone.parent_index) ?? null)
      return { ...old_bone, parent_index: new_parent }
    })
  }

  static rename_bone (bones: BoneDescriptor[], index: number, new_name: string): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`rename_bone: index ${index} out of range.`)
    }
    if (bones[index].name === new_name) return bones
    if (bones.some((b, i) => i !== index && b.name === new_name)) {
      throw new Error(`rename_bone: name "${new_name}" is already in use.`)
    }
    return bones.map((b, i) => i === index ? { ...b, name: new_name } : b)
  }

  static set_head (bones: BoneDescriptor[], index: number, head: Vec3): BoneDescriptor[] {
    return BoneTreeEditor.update_bone(bones, index, { head })
  }

  static set_tail (bones: BoneDescriptor[], index: number, tail: Vec3): BoneDescriptor[] {
    return BoneTreeEditor.update_bone(bones, index, { tail })
  }

  static set_rotation (bones: BoneDescriptor[], index: number, rotation: QuatXYZW): BoneDescriptor[] {
    return BoneTreeEditor.update_bone(bones, index, { rotation_quaternion: rotation })
  }

  static set_locked (bones: BoneDescriptor[], index: number, locked: boolean): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`set_locked: index ${index} out of range.`)
    }
    if (bones[index].locked === locked) return bones
    return BoneTreeEditor.update_bone(bones, index, { locked })
  }

  static set_ik_chain_length (bones: BoneDescriptor[], index: number, length: number): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`set_ik_chain_length: index ${index} out of range.`)
    }
    if (!Number.isInteger(length) || length < 0) {
      throw new Error('set_ik_chain_length: length must be a non-negative integer.')
    }
    const current = bones[index].ik_chain_length ?? 0
    if (current === length) return bones
    // Storing 0 as "no IK" — drop the field so the descriptor stays clean
    // when round-tripped to JSON.
    if (length === 0) {
      const next = bones.slice()
      const cleared: BoneDescriptor = { ...next[index] }
      delete cleared.ik_chain_length
      next[index] = cleared
      return next
    }
    return BoneTreeEditor.update_bone(bones, index, { ik_chain_length: length })
  }

  static set_pole_vector (bones: BoneDescriptor[], index: number, pole: Vec3 | null): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`set_pole_vector: index ${index} out of range.`)
    }
    if (pole === null) {
      const current = bones[index].pole_vector
      if (current === undefined) return bones
      const next = bones.slice()
      const cleared: BoneDescriptor = { ...next[index] }
      delete cleared.pole_vector
      next[index] = cleared
      return next
    }
    if (!pole.every(n => Number.isFinite(n))) {
      throw new Error('set_pole_vector: pole values must be finite numbers.')
    }
    return BoneTreeEditor.update_bone(bones, index, { pole_vector: [pole[0], pole[1], pole[2]] })
  }

  /**
   * Replace (or clear) the entire swing-twist rotation_limits object on a
   * bone. Pass `null` to remove all limits and let the bone rotate freely.
   */
  static set_rotation_limits (
    bones: BoneDescriptor[],
    index: number,
    limits: NonNullable<BoneDescriptor['rotation_limits']> | null
  ): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`set_rotation_limits: index ${index} out of range.`)
    }
    if (limits === null) {
      if (bones[index].rotation_limits === undefined) return bones
      const next = bones.slice()
      const cleared: BoneDescriptor = { ...next[index] }
      delete cleared.rotation_limits
      next[index] = cleared
      return next
    }
    if (limits.twist !== undefined) {
      if (!Number.isFinite(limits.twist.min_deg) || !Number.isFinite(limits.twist.max_deg)) {
        throw new Error('set_rotation_limits: twist min_deg/max_deg must be finite.')
      }
      if (limits.twist.min_deg > limits.twist.max_deg) {
        throw new Error('set_rotation_limits: twist min_deg cannot exceed max_deg.')
      }
    }
    if (limits.swing !== undefined) {
      if (!Number.isFinite(limits.swing.max_deg) || limits.swing.max_deg < 0) {
        throw new Error('set_rotation_limits: swing max_deg must be a non-negative finite number.')
      }
    }
    return BoneTreeEditor.update_bone(bones, index, { rotation_limits: limits })
  }

  static set_display_shape (
    bones: BoneDescriptor[],
    index: number,
    shape: BoneDisplayShape | null
  ): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`set_display_shape: index ${index} out of range.`)
    }
    const current = bones[index].display_shape
    if (current === shape || (shape === null && current === undefined)) return bones
    if (shape === null) {
      const next = bones.slice()
      const cleared: BoneDescriptor = { ...next[index] }
      delete cleared.display_shape
      next[index] = cleared
      return next
    }
    return BoneTreeEditor.update_bone(bones, index, { display_shape: shape })
  }

  /**
   * Apply a batch of new local rotations to multiple bones in one commit.
   * Used by the IK solver to write its solved chain rotations back to the
   * descriptor in a single undo entry.
   */
  static apply_rotations (
    bones: BoneDescriptor[],
    updates: Array<{ index: number, rotation: QuatXYZW }>
  ): BoneDescriptor[] {
    if (updates.length === 0) return bones
    const next = bones.slice()
    for (const u of updates) {
      if (u.index < 0 || u.index >= next.length) {
        throw new Error(`apply_rotations: index ${u.index} out of range.`)
      }
      next[u.index] = { ...next[u.index], rotation_quaternion: u.rotation }
    }
    return next
  }

  static reparent (bones: BoneDescriptor[], index: number, new_parent_index: number | null): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`reparent: index ${index} out of range.`)
    }
    if (new_parent_index !== null && (new_parent_index < 0 || new_parent_index >= bones.length)) {
      throw new Error(`reparent: new_parent_index ${new_parent_index} out of range.`)
    }
    if (would_create_cycle(bones, index, new_parent_index)) {
      throw new Error('reparent: would create a parent cycle.')
    }
    return BoneTreeEditor.update_bone(bones, index, { parent_index: new_parent_index })
  }

  /**
   * Mirror the bone at `root_index` and all of its descendants across the X
   * axis (left↔right). New bones are appended to the array; their names use
   * the swapped side suffix (`_l`↔`_r` or `.l`↔`.r`).
   *
   * Parenting rules for the mirrored subtree:
   *  - If a bone's parent is also being mirrored, the new bone's parent is
   *    the new mirrored counterpart.
   *  - Otherwise, if the original parent has a side-suffix counterpart that
   *    already exists in the skeleton (e.g., parent is `chest_l` and
   *    `chest_r` exists), parent the new bone to that counterpart.
   *  - Otherwise, share the original parent (typical for centerline-attached
   *    chains where the spine has no mirror).
   *
   * Throws if any bone in the subtree has no side suffix to swap, or if the
   * proposed mirrored name collides with a bone that is not part of the
   * subtree.
   */
  static mirror_bone_subtree (bones: BoneDescriptor[], root_index: number): BoneDescriptor[] {
    if (root_index < 0 || root_index >= bones.length) {
      throw new Error(`mirror_bone_subtree: index ${root_index} out of range.`)
    }

    const subtree_set = collect_descendant_indices(bones, root_index)
    subtree_set.add(root_index)
    // Process in original-index order (which is depth-first per how bones get
    // added) so parent_index references stay resolvable as we build new ones.
    const subtree_indices = Array.from(subtree_set).sort((a, b) => a - b)

    // Pre-compute mirrored names and validate collisions.
    const mirrored_names = new Map<number, string>()
    for (const i of subtree_indices) {
      const swapped = swap_side_suffix(bones[i].name)
      if (swapped === null || swapped === bones[i].name) {
        throw new Error(`Cannot mirror "${bones[i].name}" — name has no recognizable side suffix (_l/_r or .l/.r).`)
      }
      const collision = bones.findIndex((b, j) => b.name === swapped && !subtree_set.has(j))
      if (collision >= 0) {
        throw new Error(`Mirror would collide: a bone named "${swapped}" already exists outside the mirrored subtree.`)
      }
      mirrored_names.set(i, swapped)
    }

    // Build the mirrored bones, tracking old→new index mapping for parenting.
    const old_to_new_index = new Map<number, number>()
    const mirrored: BoneDescriptor[] = []
    for (const i of subtree_indices) {
      old_to_new_index.set(i, bones.length + mirrored.length)
    }

    for (const i of subtree_indices) {
      const original = bones[i]
      const new_parent_index = compute_mirrored_parent_index(bones, original.parent_index, old_to_new_index)
      const head: Vec3 = [-original.head[0], original.head[1], original.head[2]]
      const tail: Vec3 = [-original.tail[0], original.tail[1], original.tail[2]]
      // X-axis quaternion mirror: (-x, y, z, -w). Equivalent to flipping the
      // axis-of-rotation's X component AND reversing handedness.
      const q = original.rotation_quaternion
      const new_rot: QuatXYZW = [-q[0], q[1], q[2], -q[3]]
      mirrored.push({
        name: mirrored_names.get(i) as string,
        parent_index: new_parent_index,
        head,
        tail,
        rotation_quaternion: new_rot,
        ...(original.locked !== undefined ? { locked: original.locked } : {})
      })
    }

    return [...bones, ...mirrored]
  }

  /**
   * Suggest a unique name in the form `${prefix}_${n}` where n is the smallest
   * non-negative integer that doesn't collide with an existing bone name.
   */
  static suggest_unique_name (bones: BoneDescriptor[], prefix: string = 'bone'): string {
    const used = new Set(bones.map(b => b.name))
    for (let n = 0; n < 10_000; n++) {
      const candidate = `${prefix}_${n}`
      if (!used.has(candidate)) return candidate
    }
    return `${prefix}_${Date.now()}`
  }

  private static update_bone (
    bones: BoneDescriptor[],
    index: number,
    patch: Partial<BoneDescriptor>
  ): BoneDescriptor[] {
    if (index < 0 || index >= bones.length) {
      throw new Error(`update_bone: index ${index} out of range.`)
    }
    return bones.map((b, i) => i === index ? { ...b, ...patch } : b)
  }
}

function collect_descendant_indices (bones: BoneDescriptor[], root_index: number): Set<number> {
  const descendants = new Set<number>()
  const stack: number[] = [root_index]
  while (stack.length > 0) {
    const current = stack.pop() as number
    for (let i = 0; i < bones.length; i++) {
      if (bones[i].parent_index === current && !descendants.has(i) && i !== root_index) {
        descendants.add(i)
        stack.push(i)
      }
    }
  }
  return descendants
}

/**
 * Swap the side suffix of a bone name. Recognizes `_l`/`_r` and `.l`/`.r`
 * (case-insensitive on the letter, preserves the original case of the
 * separator). Returns `null` if no recognized suffix is present.
 */
export function swap_side_suffix (name: string): string | null {
  const patterns: Array<[RegExp, (match: RegExpExecArray) => string]> = [
    [/(_)([lL])$/, (m) => name.slice(0, -2) + m[1] + (m[2] === 'l' ? 'r' : 'R')],
    [/(_)([rR])$/, (m) => name.slice(0, -2) + m[1] + (m[2] === 'r' ? 'l' : 'L')],
    [/(\.)([lL])$/, (m) => name.slice(0, -2) + m[1] + (m[2] === 'l' ? 'r' : 'R')],
    [/(\.)([rR])$/, (m) => name.slice(0, -2) + m[1] + (m[2] === 'r' ? 'l' : 'L')]
  ]
  for (const [regex, swap] of patterns) {
    const match = regex.exec(name)
    if (match !== null) return swap(match)
  }
  return null
}

/**
 * Transform a point in the deleted bone's local frame into the deleted bone's
 * parent frame. Math: p_in_parent = deleted_head + rotate(deleted_quat, p).
 * Assumes uniform scale = 1 (the descriptor model doesn't track per-bone
 * scale).
 */
function transform_point_into_parent_frame (point: Vec3, parent_head: Vec3, parent_quat: QuatXYZW): Vec3 {
  const rotated = rotate_vector_by_quaternion(point, parent_quat)
  return [
    parent_head[0] + rotated[0],
    parent_head[1] + rotated[1],
    parent_head[2] + rotated[2]
  ]
}

/**
 * Optimized formula for q * v * q⁻¹ that avoids constructing intermediate
 * quaternions. v' = 2(u·v)u + (s² − u·u)v + 2s(u × v) where u = (qx, qy, qz)
 * and s = qw. Equivalent to Three.js's Vector3.applyQuaternion.
 */
function rotate_vector_by_quaternion (v: Vec3, q: QuatXYZW): Vec3 {
  const ux = q[0]; const uy = q[1]; const uz = q[2]; const s = q[3]
  const vx = v[0]; const vy = v[1]; const vz = v[2]
  const dot_uv = ux * vx + uy * vy + uz * vz
  const dot_uu = ux * ux + uy * uy + uz * uz
  const cross_x = uy * vz - uz * vy
  const cross_y = uz * vx - ux * vz
  const cross_z = ux * vy - uy * vx
  const f1 = 2 * dot_uv
  const f2 = s * s - dot_uu
  const f3 = 2 * s
  return [
    f1 * ux + f2 * vx + f3 * cross_x,
    f1 * uy + f2 * vy + f3 * cross_y,
    f1 * uz + f2 * vz + f3 * cross_z
  ]
}

/**
 * Hamilton quaternion product: r = a * b applies b first then a (standard
 * Three.js convention). Used to compose a child's local rotation with its
 * parent's rotation when collapsing the parent out of the chain.
 */
function quaternion_multiply (a: QuatXYZW, b: QuatXYZW): QuatXYZW {
  const ax = a[0]; const ay = a[1]; const az = a[2]; const aw = a[3]
  const bx = b[0]; const by = b[1]; const bz = b[2]; const bw = b[3]
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz
  ]
}

/**
 * Determine the parent_index for a mirrored bone. If the original parent is
 * also being mirrored, point at the new mirror. Otherwise look for an
 * existing side-counterpart bone in the skeleton and use it; failing that,
 * fall back to the original parent (typical when the parent is on the
 * centerline, e.g., spine).
 */
function compute_mirrored_parent_index (
  bones: BoneDescriptor[],
  original_parent_index: number | null,
  old_to_new_index: Map<number, number>
): number | null {
  if (original_parent_index === null) return null
  const remapped = old_to_new_index.get(original_parent_index)
  if (remapped !== undefined) return remapped
  const swapped_parent_name = swap_side_suffix(bones[original_parent_index].name)
  if (swapped_parent_name !== null) {
    const counterpart = bones.findIndex(b => b.name === swapped_parent_name)
    if (counterpart >= 0) return counterpart
  }
  return original_parent_index
}
